/* eslint-disable react/prop-types */
/**
 * MCCIntegrityBanner
 * ==================
 * Phase-4 FOH banner that surfaces MCC-driven integrity warnings on
 * the pricing screen. The warning list comes pre-computed from the
 * backend (`GET /api/foh/yestimate/{id}.mcc_integrity_warnings`).
 *
 * Renders nothing when there are zero warnings. Otherwise shows a
 * compact card grouped by severity (warn → red, info → amber) with
 * the JG title and a short actionable message.
 */
import { AlertTriangle, Info, ShieldCheck } from "lucide-react";

export default function MCCIntegrityBanner({ warnings, jobGroups }) {
  const list = Array.isArray(warnings) ? warnings : [];
  if (list.length === 0) {
    // Friendly success state — only render when there's at least one
    // DVSA-sourced job_group on the yestimate (otherwise it's noise).
    const hasDvsa = (jobGroups || []).some(
      (jg) => jg?.source === "dvsa_mot_result"
    );
    if (!hasDvsa) return null;
    return (
      <div
        className="flex items-center gap-2 px-3 py-2 mb-3 rounded-lg bg-emerald-50 border border-emerald-200"
        data-testid="mcc-integrity-banner-ok"
      >
        <ShieldCheck className="w-4 h-4 text-emerald-700" />
        <span className="text-xs font-semibold text-emerald-800">
          MCC integrity checks passed — alignment, pairs and route
          dependencies all match the tester's capture.
        </span>
      </div>
    );
  }

  const titleFor = (jgId) => {
    const jg = (jobGroups || []).find((j) => j.id === jgId);
    return jg?.title || jg?.label || "Item";
  };

  return (
    <div
      className="mb-3 rounded-lg border border-amber-300 bg-amber-50 overflow-hidden"
      data-testid="mcc-integrity-banner"
    >
      <div className="flex items-center gap-2 px-3 py-2 border-b border-amber-200 bg-amber-100">
        <AlertTriangle className="w-4 h-4 text-amber-800" />
        <span className="text-xs font-bold text-amber-900 uppercase tracking-wide">
          MCC integrity — {list.length}{" "}
          {list.length === 1 ? "warning" : "warnings"}
        </span>
      </div>
      <ul className="divide-y divide-amber-200">
        {list.map((w, i) => (
          <li
            key={`${w.job_group_id}-${w.code}-${i}`}
            className="px-3 py-2"
            data-testid={`mcc-integrity-warning-${w.code}`}
          >
            <div className="flex items-start gap-2">
              {w.severity === "info" ? (
                <Info className="w-3.5 h-3.5 text-amber-700 mt-0.5 shrink-0" />
              ) : (
                <AlertTriangle className="w-3.5 h-3.5 text-red-700 mt-0.5 shrink-0" />
              )}
              <div className="min-w-0 flex-1">
                <p className="text-xs font-semibold text-amber-900">
                  {w.title}
                  <span className="font-normal text-amber-700 ml-1">
                    · {titleFor(w.job_group_id)}
                  </span>
                </p>
                <p className="text-[11px] text-amber-900/90 leading-snug mt-0.5">
                  {w.message}
                </p>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
